import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChartSpline, ArrowRight, Flame, CalendarDays, Wallet, Users, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DeltaBadge, fadeUp } from "@/components/exec/KpiTile";
import { AccuracyMeter } from "@/components/exec/ForecastCharts";
import { useScope } from "@/lib/scope";
import { fmtInt, fmtMYRShort, fmtDate, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

const GRADE_TONE = {
  positive: "border-success/30 bg-success/10 text-success",
  warning: "border-warning/30 bg-warning/10 text-warning",
  negative: "border-danger/30 bg-danger/10 text-danger",
  neutral: "border-border/70 bg-muted/20 text-muted-foreground",
};
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayLabel = (iso) => (iso ? `${DAY[new Date(`${iso}T00:00:00`).getDay()]} ${fmtDate(iso)}` : "");

const Cell = ({ icon: Icon, label, value, sub, testId, children }) => (
  <div className="min-w-0 rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{Icon && <Icon size={12} className="text-gold/80" />}<span className="truncate">{label}</span></div>
    <div className="font-display text-lg md:text-xl tabular leading-tight mt-1">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
    {children}
  </div>
);

/**
 * Executive Summary card: the next 30 days in one glance - expected footfall, revenue (where POS exists) and the
 * forecast's own self-check score. Links through to the full Forecast page.
 */
export const ForecastBriefCard = ({ query, className }) => {
  const navigate = useNavigate();
  const { setScope } = useScope();
  const b = query.data;
  const open = () => {
    setScope("all");
    navigate("/forecast");
  };

  return (
    <motion.section variants={fadeUp} initial="hidden" animate="show" className={cn("card-surface p-4 md:p-5 relative overflow-hidden", className)} data-testid="summary-forecast-card">
      <span className="absolute inset-x-4 top-0 h-px bg-gold/25" />
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow">Looking ahead · next 30 days</div>
          <h3 className="font-display text-base md:text-lg leading-tight mt-0.5 inline-flex items-center gap-2"><ChartSpline size={16} className="text-gold" /> {b?.available ? `About ${fmtInt(b.visitors.value)} visitors expected across the group` : "Footfall and revenue forecast"}</h3>
          {b?.available && <p className="text-xs text-muted-foreground mt-1 leading-relaxed" data-testid="summary-forecast-sub">{fmtDate(b.horizon.start)} – {fmtDate(b.horizon.end)} · 80% range {fmtInt(b.visitors.lo)} – {fmtInt(b.visitors.hi)} · {b.stores_included} of {b.stores_total} stores forecast · issued on counts to {fmtDate(b.anchor)}.</p>}
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs shrink-0" onClick={open} data-testid="summary-open-forecast">
          Open forecast <ArrowRight size={13} />
        </Button>
      </div>

      {query.isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2" data-testid="summary-forecast-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-[76px] rounded-lg" />)}
        </div>
      )}

      {query.isError && (
        <div className="rounded-lg border border-dashed border-danger/40 bg-danger/5 p-3 text-xs text-muted-foreground flex items-center justify-between gap-3" data-testid="summary-forecast-error">
          <span>The forecast could not be loaded right now.</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => query.refetch()}>Retry</Button>
        </div>
      )}

      {b && !b.available && (
        <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 p-3 text-xs text-muted-foreground" data-testid="summary-forecast-unavailable">
          {b.reason || "Not enough history to forecast yet."} The forecast switches on once a store has two weeks of closed counts.
        </div>
      )}

      {b?.available && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <Cell icon={Users} label="Avg visitors / day" value={fmtInt(b.avg_daily.value)} testId="summary-forecast-avg">
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {b.avg_daily.delta !== null && b.avg_daily.delta !== undefined ? <DeltaBadge delta={b.avg_daily.delta} tone={b.avg_daily.delta > 2 ? "positive" : b.avg_daily.delta < -2 ? "negative" : "neutral"} testId="summary-forecast-avg-delta" /> : null}
              <span className="text-[11px] text-muted-foreground">vs last 28 days</span>
            </div>
          </Cell>
          <Cell icon={Wallet} label="Expected revenue" value={b.revenue ? fmtMYRShort(b.revenue.value) : "—"} sub={b.revenue ? `range ${fmtMYRShort(b.revenue.lo)} – ${fmtMYRShort(b.revenue.hi)} · ${b.revenue_stores} POS store${b.revenue_stores === 1 ? "" : "s"}` : "needs POS receipts"} testId="summary-forecast-revenue" />
          <Cell icon={Gauge} label="Self-check accuracy" value={b.accuracy.backtest_7d !== null && b.accuracy.backtest_7d !== undefined ? `${b.accuracy.backtest_7d}%` : "—"} testId="summary-forecast-accuracy">
            <div className="mt-1.5 flex items-center gap-2">
              <Badge variant="outline" className={cn("text-[10px] font-mono px-1.5", GRADE_TONE[b.accuracy.grade.tone] || GRADE_TONE.neutral)} data-testid="summary-forecast-grade">grade {b.accuracy.grade.grade}</Badge>
              <span className="text-[11px] text-muted-foreground truncate">{b.accuracy.tested_days ? `${b.accuracy.tested_days} days tested` : "1 week ahead"}</span>
            </div>
          </Cell>
          <Cell icon={Flame} label="Busiest day" value={fmtInt(b.peak.value)} sub={`${dayLabel(b.peak.date)} · ${b.peak.why}`} testId="summary-forecast-peak" />
          <Cell icon={CalendarDays} label="Public holidays" value={String(b.holiday_days)} sub={b.next_holidays?.[0] ? `next: ${b.next_holidays[0].names[0] || "holiday"} ${fmtDate(b.next_holidays[0].date)}` : "none in the window"} testId="summary-forecast-holidays" />
          <Cell icon={Users} label="Next 7 days" value={fmtInt(b.week.visitors)} sub={`range ${fmtInt(b.week.lo)} – ${fmtInt(b.week.hi)} · weekend ${fmtPct(b.weekend_share, 0)} of month`} testId="summary-forecast-week" />
        </div>
      )}

      {b?.available && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
          <AccuracyMeter label="Accuracy one week ahead · rolling backtest against days that have already happened" value={b.accuracy.backtest_7d} testId="summary-forecast-meter" />
          {b.top_insight && <div className="text-[11px] text-muted-foreground md:max-w-[360px] md:text-right leading-snug" data-testid="summary-forecast-insight">{b.top_insight}</div>}
        </div>
      )}
    </motion.section>
  );
};
